import PropTypes from 'prop-types';
import React from 'react';
import { bindAll } from '@bibliocommons/utils-react';
import Immutable from 'immutable';
import { withRouter } from 'app/helpers/routing';
import BibScreenReaderDescription from 'app/components/shared/BibScreenReaderDescription';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { PrimaryButton } from '@bibliocommons/deprecated-base-buttons';
import { Plus, Arrows, Spinner } from '@bibliocommons/deprecated-base-icons';
import { routerShape } from '@bibliocommons/bc-prop-types';
import { CatalogueResultItemButton, CatalogueResultItemAlert } from 'app/components/shared/AddTitle/components';
import { AddTitleOverlay } from '../../shared/AddTitle/AddTitleOverlay';
import AddTitleContainer from '../../shared/AddTitle/AddTitleContainer';

import './ShelfAddTitleButton.scss';

export class ShelfAddTitleButton extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      showEditor: false,
      itemsAddedHash: {},
      isUpdatingResultItem: false,
      anyTransactionSuccess: false // determines if at least one transaction has been successful
    };
  }

  closeEditor() {
    this.setState({ showEditor: false });
    if (this.state.anyTransactionSuccess) {
      const { router, ownerId, shelfActions, currentShelfName } = this.props;
      shelfActions.searchUserShelf(currentShelfName, ownerId, {
        ...router.query,
        page: undefined
      });
    }
  }

  openEditor() {
    this.setState({ showEditor: true, anyTransactionSuccess: false });
  }

  handleAdd(item) {
    return () => {
      const { shelfActions, currentShelfName } = this.props;
      this.setState({ isUpdatingResultItem: true });

      shelfActions.addToShelf({
        metadataId: item.get('metadataId'),
        shelfToAdd: currentShelfName,
        postSuccess: () => {
          this.setState(prevState => ({
            itemsAddedHash: {
              ...prevState.itemsAddedHash,
              [item.get('metadataId')]: true
            },
            isUpdatingResultItem: false,
            anyTransactionSuccess: true
          }));
        }
      });
    };
  }

  handleMove(item) {
    return () => {
      const { shelfActions, currentShelfName } = this.props;
      shelfActions.updateShelfItems({
        shelfName: item.get('shelf'),
        shelfItemIds: [item.get('id')],
        metadataIds: [item.get('metadataId')],
        newShelfName: currentShelfName,
        privateItem: item.get('privateItem'),
        postSuccess: () => {
          this.setState(prevState => ({
            itemsAddedHash: {
              ...prevState.itemsAddedHash,
              [item.get('metadataId')]: true
            },
            isUpdatingResultItem: false,
            anyTransactionSuccess: true
          }));
        }
      });

      this.setState({ isUpdatingResultItem: true });
    };
  }

  renderOverlay() {
    return (
      this.state.showEditor && (
        <AddTitleContainer
          handleClose={this.closeEditor}
          renderAddOn={this.renderAddOn}
          isUpdatingResultItem={this.state.isUpdatingResultItem}
          isWrappedInForm
        />
      )
    );
  }

  renderScreenReaderMessage(item) {
    const shelfLabelEnum = {
      completed: 'label_my_shelf_completed',
      in_progress: 'label_my_shelf_in_progress',
      for_later: 'label_my_shelf_for_later'
    };

    return (
      <FormattedMessage
        id="ada_label_add_item_to_list_type"
        values={{
          title: <BibScreenReaderDescription bibBrief={item} />,
          listType: <FormattedMessage id={shelfLabelEnum[this.props.currentShelfName]} />
        }}
      />
    );
  }

  renderAddOn(item) {
    const { currentUserShelves, currentShelfName, shelfTransactions } = this.props;
    const currentShelfItem = currentUserShelves.get(item.get('metadataId'));
    const shelfItemTransaction = shelfTransactions.get(item.get('metadataId'), Immutable.Map());

    if (shelfItemTransaction.get('isAdding') || shelfItemTransaction.get('isMoving')) {
      // show spinner since transaction is being made
      return <Spinner />;
    }
    if (currentShelfItem) {
      if (currentShelfItem.get('shelf') === currentShelfName) {
        // item is on current shelf, alert that it has been added/moved
        const alertLabel = this.state.itemsAddedHash[item.get('metadataId')] ? 'added_to_shelf' : 'on_shelf';
        return <CatalogueResultItemAlert label={<FormattedMessage id={alertLabel} />} />;
      }

      // prompt to move item to this shelf
      return (
        <CatalogueResultItemButton
          icon={<Arrows.Move.Right className="result-item-icon" />}
          label={<FormattedMessage id="move_to_this_shelf" />}
          item={item}
          screenReaderMessage={this.renderScreenReaderMessage(item)}
          handleClick={this.handleMove(currentShelfItem)}
        />
      );
    }

    // prompt to add item to this shelf
    return (
      <CatalogueResultItemButton
        icon={<Plus />}
        label={<FormattedMessage id="add_to_shelf" />}
        item={item}
        screenReaderMessage={this.renderScreenReaderMessage(item)}
        handleClick={this.handleAdd(item)}
      />
    );
  }

  render() {
    return (
      <PrimaryButton handleClick={this.openEditor} className="cp-shelf-add-title-button" dataKey="add-title-button">
        <Plus /> <FormattedMessage id="label_add_title" />
        <AddTitleOverlay open={!!this.state.showEditor} handleClose={this.closeEditor}>
          {this.renderOverlay()}
        </AddTitleOverlay>
      </PrimaryButton>
    );
  }
}

ShelfAddTitleButton.propTypes = {
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentShelfName: PropTypes.string.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  currentUserShelves: ImmutablePropTypes.map.isRequired,
  ownerId: PropTypes.number.isRequired,
  router: routerShape.isRequired
};

export default withRouter(ShelfAddTitleButton);
